import 'react-dates/initialize'
import {
  type ReactElement,
  Children,
  isValidElement,
  useState,
  useEffect,
  lazy,
  Suspense,
} from 'react'
import { Router, matchPath } from 'react-router'
import { useLocation } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client'
import { QueryClientProvider } from '@tanstack/react-query'
import flow from 'lodash-es/flow'
import map from 'lodash-es/map'
import compact from 'lodash-es/compact'
import flatten from 'lodash-es/flatten'
import Favicon from 'react-favicon'
import {
  I18nProvider,
  useI18nContext,
  type IPP_SUPPORTED_LOCALE,
  type IPP_TRANSLATION_KEY,
  IPP_DEFAULT_LOCALE,
  LOCALE_TO_TRANSLATION_KEY,
  getStoredLocaleWithFallback,
} from '@retailer-platform/shared-components/src/utils/intl'
import EN_US_TRANSLATIONS from '../../intl/translations/en-US.json'
import ES_ES_TRANSLATIONS from '../../intl/translations/es-ES.json'
import FR_FR_TRANSLATIONS from '../../intl/translations/fr-FR.json'
import {
  AppProvider,
  importAppUIProviders,
} from '../../gin-and-tonic/containers/app-provider/AppProvider'
import { AppProviderPreloader } from '../../gin-and-tonic/containers/app-provider/components/AppProviderPreloader'
// Deep path, NOT the '@retailer-platform/shared-components' barrel: this file is in
// the eager boot graph, and the barrel is non-tree-shaken (~1.4 MB). LoadingDots
// pulls only @emotion/styled (already eager) + color tokens, so it's ~free for boot.
import { LoadingDots } from '@retailer-platform/shared-components/src/common/loading-dots/LoadingDots'
import { errors } from '../error-handling/errors'
import { fetchDashboardClient } from '../../legacy/apollo/dashboardClient'
import { queryClient } from '../query-client/queryClient'
import createHistory from '../../legacy/common/history'
import { ROUTES_BY_NAME } from '../routing/routes'
import { isTanstackShellEnabled } from '../routing/tanstack/shellFlag'
// Type-only: elided at build, so the flag-off boot graph stays @tanstack-free.
import type { AnyRoute } from '@tanstack/react-router'

// Lazy so the flag-off boot pulls zero @tanstack/react-router bytes — the shell
// chunk loads only when the flag is on.
const TanstackShell = lazy(() => import('../routing/tanstack/tanstackShell'))
import RPP_FAVICON from '../../assets/favicon.png'
import { setTotemRouteOwnerMapping } from '../events/analytics'
import type { Instacart_Infra_Totem_V1_System as TotemSystem } from '../../__codegen__/api'
import { createMessagesWithFallback } from '../../intl/intl.utils'
import { loadDayjsLocale } from '../query-params/dayjsDate.utils'
import { loadMomentLocale } from '../query-params/momentDate.utils'
import { DASHBOARD_ROUTE_TOOLS } from '../routing/dashRouteTools'
import { type NavEntryPointV3 } from '../../gin-and-tonic/containers/nav-v3/navEntryPointsV3'
import { type AdminNavEntryPointV3 } from '../../gin-and-tonic/containers/nav-v3/adminNavEntryPointsV3'
import type { SubNavItemConfig } from '../../legacy/components/Nav/navConfig'
import type { AccessControlConfig } from '../../legacy/components/AccessControl/accessControl.utils'
import { CoreContext } from './RPPCoreContext'
import {
  type RPPDomain,
  type RPPCoreContext,
  type RPPRouter,
  type RPPNavigationEntry,
  type RPPDomainNavigationEntryData,
  type RPPDomainNavigationMetadata,
} from './RPPCore.types'

export interface CatalogDepsManifestEntry {
  requires?: string[]
  shellRequires?: string[]
}

export interface CatalogDepsManifest {
  domains: Record<string, CatalogDepsManifestEntry>
}

/**
 * This class represents our base application, where multiple domains
 * are registered, and a context generated based on all defined domains
 * which is then passed throughout the application.
 */
export class RPPCore {
  domains: RPPDomain<any, any, any>[]
  additionalTranslations: Record<IPP_SUPPORTED_LOCALE, Record<string, string>>
  /**
   * Dev-only IntlProvider onError: fails loud on missing message ids (the
   * raw-id incident class under route-scoped catalogs). The hook lives in
   * apps/ipp (it needs the generated id→owner manifest) and is injected via
   * `setDevIntlOnError`; undefined in prod, so prod behavior is unchanged.
   */
  devIntlOnError?: Parameters<typeof AppProvider>[0]['onError']
  // Lazy loader for migrated domains' native routes; `packages/dashboard` can't import
  // `domains/*`, so the app injects them. Invoked only by the flag-on shell.
  loadNativeRoutes?: () => Promise<readonly AnyRoute[]>

  constructor({
    domains = [],
    loadNativeRoutes,
  }: {
    domains?: RPPDomain<any, any, any>[]
    loadNativeRoutes?: () => Promise<readonly AnyRoute[]>
  }) {
    this.domains = domains
    this.loadNativeRoutes = loadNativeRoutes
    this.additionalTranslations = {
      'en-US': {},
      'es-ES': {},
      'es-US': {},
      'fr-FR': {},
      'fr-CA': {},
    }
  }

  /**
   * Inject the dev-only missing-message fail-loud hook (apps/ipp supplies it;
   * dashboard cannot import from the app). No-op once set; pass undefined to
   * clear.
   */
  setDevIntlOnError(onError: RPPCore['devIntlOnError']): void {
    this.devIntlOnError = onError
  }

  attachDomain(domain: RPPDomain<any, any, any>) {
    this.domains.push(domain)
  }

  /** Boot URL data is supplied by the app so domain implementations can stay deferred. */
  private routeCatalog?: Readonly<Record<string, string>>

  setRouteCatalog(routes: Readonly<Record<string, string>>): void {
    this.routeCatalog = routes
  }

  /** Eager, data-only retailer nav entries; apps supplies this without loading domain code. */
  private navEntriesManifest?: readonly RPPDomainNavigationMetadata[]

  setNavEntriesManifest(manifest: readonly RPPDomainNavigationMetadata[]): void {
    this.navEntriesManifest = manifest
  }

  /**
   * Lazy loader for the full legacy domain registry (flag-on only — the shell defers it
   * until a legacy URL needs the RRv5 tree). apps/ipp supplies the thunk.
   */
  private legacyDomainsLoader?: () => Promise<{ registerDomains: (app: RPPCore) => void }>
  private legacyDomainsPromise?: Promise<void>
  private domainsLoadedListeners: Array<() => void> = []

  setLegacyDomainsLoader(loader: () => Promise<{ registerDomains: (app: RPPCore) => void }>): void {
    this.legacyDomainsLoader = loader
  }

  /**
   * Load + register the full legacy registry once. Subsequent calls return the same
   * promise. Notifies `onDomainsLoaded` listeners when the new domains are attached.
   */
  ensureLegacyDomains(): Promise<void> {
    this.legacyDomainsPromise ??= Promise.resolve(
      this.legacyDomainsLoader?.() ?? { registerDomains: () => undefined }
    )
      .then(({ registerDomains }) => {
        registerDomains(this)
        this.attachCatalogDeps()
        // Shell-required catalogs: start() only ran this for the domains registered at
        // boot (flag-on: just the visited one) — the deferred registry's domains need
        // the same treatment once they attach.
        return this.loadShellRequires()
      })
      .then(() => {
        // Path lookups cached before the registry attached are stale (they saw only the
        // minimal on-demand domains) — drop them so the next lookup re-derives.
        this.domainsForPathCache.clear()
        for (const cb of this.domainsLoadedListeners) cb()
      })
      .catch(error => {
        // A rejected registry chunk-load must not poison the cached promise forever —
        // the next ensureLegacyDomains() call retries the import instead of replaying
        // the rejection (the catch-all renders the error state meanwhile).
        this.legacyDomainsPromise = undefined
        throw error
      })
    return this.legacyDomainsPromise
  }

  /**
   * Register a single domain at on-demand entry (the native shell calls this for the
   * visited domain only). Notifies listeners so CoreContext re-prepares with the new
   * domain's routes/translations.
   */
  registerOnDemandDomain(domain: RPPDomain<any, any, any>): void {
    // Dedupe: two callers register the visited domain (index.tsx's loadNativeRoutes
    // thunk AND domainRegistration's bootstrap); a bare push would double every
    // registration side effect (translations, catalog deps) for that domain.
    if (!this.domains.some(registered => registered.id === domain.id)) {
      this.attachDomain(domain)
      this.attachCatalogDeps()
      // Same staleness as ensureLegacyDomains: paths looked up before this domain
      // attached must re-derive.
      this.domainsForPathCache.clear()
      for (const cb of this.domainsLoadedListeners) cb()
    }
  }

  /** Subscribe to domain-registration changes (CoreContext re-prepare). */
  onDomainsLoaded(cb: () => void): void {
    this.domainsLoadedListeners.push(cb)
  }

  /**
   * Generated domain → catalog-requirement manifest (apps/ipp injects it —
   * dashboard cannot import from the app). Attached to each domain after
   * registration; domains absent from the manifest simply have no deps.
   */
  private catalogDepsManifest?: CatalogDepsManifest

  /**
   * Inject the generated domain-catalog-deps manifest
   * (apps/ipp/src/nav-labels/__generated__/domainCatalogDeps.json).
   */
  setCatalogDepsManifest(manifest: RPPCore['catalogDepsManifest']): void {
    this.catalogDepsManifest = manifest
  }

  /**
   * Attach generated catalogDeps to every registered domain. Called once after
   * registerDomains in start(); safe to call again (idempotent overwrite).
   */
  attachCatalogDeps(): void {
    if (!this.catalogDepsManifest) return
    for (const domain of this.domains) {
      const entry = this.catalogDepsManifest.domains[domain.id]
      domain.catalogDeps = entry?.requires ?? []
    }
  }

  getLocaleMessages<T extends Record<string, string>>(
    locale: IPP_SUPPORTED_LOCALE,
    messages: {
      'en-US': T
      'es-ES'?: Partial<T>
      'es-US'?: Partial<T>
      'fr-FR'?: Partial<T>
      'fr-CA'?: Partial<T>
    }
  ): T {
    const fallback = messages[IPP_DEFAULT_LOCALE] as T
    if (locale === IPP_DEFAULT_LOCALE) {
      return fallback
    }
    const partialMessages = messages[locale] || {}
    return createMessagesWithFallback(partialMessages as T, fallback)
  }

  getDashboardTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    return this.getLocaleMessages(locale, {
      'en-US': EN_US_TRANSLATIONS,
      'es-ES': ES_ES_TRANSLATIONS,
      'fr-FR': FR_FR_TRANSLATIONS,
    })
  }

  getAdditionalTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    return this.getLocaleMessages(locale, this.additionalTranslations)
  }

  getDomainTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    return this.domains.map(domain =>
      this.getLocaleMessages(locale, {
        'en-US': domain.translations?.EN_US || {},
        'es-ES': domain.translations?.ES_ES || {},
        'fr-FR': domain.translations?.FR_FR || {},
      })
    )
  }

  /**
   * The catalog keys a route needs: the en-US base (fallback for any missing string) plus
   * the active locale's catalog. Deduped, so an en-US user gets just ['EN_US'].
   */
  private routeCatalogKeys(locale: IPP_SUPPORTED_LOCALE): IPP_TRANSLATION_KEY[] {
    const localeKey = LOCALE_TO_TRANSLATION_KEY[locale]
    return localeKey === 'EN_US' ? ['EN_US'] : ['EN_US', localeKey]
  }

  /**
   * Resolves one domain's lazy translation payload for a catalog key and back-fills
   * `domain.translations` so getDomainTranslations() can read it synchronously.
   * No-ops when already present or when the domain ships that catalog eagerly.
   */
  private async loadDomainLocale(
    domain: RPPDomain<any, any, any>,
    localeKey: IPP_TRANSLATION_KEY
  ): Promise<void> {
    if (domain.translations[localeKey]) return
    const loader = domain.lazyTranslations?.[localeKey]
    // A domain that ships no lazy loader for this key legitimately has nothing to
    // load (e.g. en-US-only domains have no ES_ES loader) — that's the no-op case.
    if (!loader) return
    // Reaching here means a loader exists, so a rejection is a real chunk-load
    // failure (network / stale deploy), not a missing file. Let it propagate so
    // the route gate can surface a reload instead of silently wedging a blank page.
    const module = await loader()
    domain.translations[localeKey] = module.default
    // Merged catalog is stale — force a rebuild on next getTranslations().
    this.translationsCache = {}
  }

  /**
   * The domains whose catalogs a pathname can require. Route ownership does not
   * prove which domain's components ultimately render: domains share routes,
   * and a route's page can render components exported from other domains
   * (whose catalog literals live in the component's own file). So the set is
   * the route-matching domains (most-specific first, but ALL match — shared
   * approval routes load both owners) plus the generated per-domain
   * `catalogDeps` closure, which the domain-catalog-deps manifest derives
   * statically from cross-domain component composition.
   */
  private domainsForPathCache = new Map<string, RPPDomain<any, any, any>[]>()

  private domainsForLoad(pathname: string): RPPDomain<any, any, any>[] {
    const cached = this.domainsForPathCache.get(pathname)
    if (cached) return cached

    const owners: RPPDomain<any, any, any>[] = []
    for (const domain of this.domains) {
      if (
        Object.values<string>(domain.routes).some(pattern =>
          matchPath(pathname, { path: pattern, exact: false })
        )
      ) {
        owners.push(domain)
      }
    }

    // Catalog deps of the route owners: component-composition requirements the
    // route's own domain cannot satisfy. Missing deps resolve to nothing — a
    // domain without generated deps (or an unknown id) just has none.
    const depIds = new Set(owners.flatMap(domain => domain.catalogDeps ?? []))
    const depDomains = this.domains.filter(
      domain => depIds.has(domain.id) && !owners.includes(domain)
    )

    const result = [...owners, ...depDomains]
    this.domainsForPathCache.set(pathname, result)
    return result
  }

  /**
   * Whether every catalog the active route requires is already loaded — the
   * route-matching domains plus their generated catalogDeps — so the page can
   * render without a raw message-id flash: en-US (base/fallback) plus the
   * active locale's catalog per domain. True for domains that ship those
   * catalogs eagerly (no lazy loader).
   */
  routeTranslationsLoaded(pathname: string, locale: IPP_SUPPORTED_LOCALE): boolean {
    const domains = this.domainsForLoad(pathname)

    return this.routeCatalogKeys(locale).every(key =>
      domains.every(domain => !domain.lazyTranslations?.[key] || !!domain.translations[key])
    )
  }

  /**
   * Loads every catalog the route requires: for each route-matching domain plus
   * its generated catalogDeps closure — always its en-US base, plus the active
   * locale's catalog. Bound to route entry so only the visited domain's strings
   * (and the strings of domains whose components it renders) transfer — the
   * other domains' en-US never ride boot.
   *
   * en-US is required (it is the fallback for every string); its failure rejects
   * so the gate can surface a reload. The active locale's catalog is optional:
   * a failure degrades to the en-US base rather than dead-ending a page that
   * could render in English.
   */
  async loadTranslationsForRoute(pathname: string, locale: IPP_SUPPORTED_LOCALE): Promise<void> {
    const domains = this.domainsForLoad(pathname)

    const [baseKey, ...localeKeys] = this.routeCatalogKeys(locale)

    await Promise.all(
      domains.map(async domain => {
        // en-US base: a rejection here has no fallback, so let it propagate to the gate.
        await this.loadDomainLocale(domain, baseKey)

        // Non-en-US catalog: on failure, mark the key loaded-but-empty so the route reads
        // as ready and react-intl falls back to en-US per key. No in-place retry — the same
        // chunk url re-rejects from the module cache; only a full reload re-fetches.
        await Promise.all(
          localeKeys.map(key =>
            this.loadDomainLocale(domain, key).catch((error: unknown) => {
              errors.captureException(
                error as Error,
                `Failed to load ${key} translations for domain ${domain.id}; falling back to en-US`
              )
              domain.translations[key] = {}
            })
          )
        )
      })
    )
  }

  /**
   * Loads catalogs for domains whose components the eager dashboard shell
   * renders from global menus (recorded as `shellRequires` in the generated
   * manifest — self-listing). No-op until a domain flips to lazy EN_US.
   */
  async loadShellRequires(): Promise<void> {
    const shellDomainIds = new Set(
      Object.entries(this.catalogDepsManifest?.domains ?? {})
        .filter(([, entry]) => entry.shellRequires?.length)
        .map(([id]) => id)
    )
    await Promise.all(
      this.domains
        .filter(domain => shellDomainIds.has(domain.id))
        .map(domain => this.loadDomainLocale(domain, 'EN_US'))
    )
  }

  // Cache for merged translations by locale
  private translationsCache: Partial<Record<IPP_SUPPORTED_LOCALE, Record<string, string>>> = {}

  getTranslations(locale: IPP_SUPPORTED_LOCALE = IPP_DEFAULT_LOCALE) {
    // Return cached translations if available
    if (this.translationsCache[locale]) {
      return this.translationsCache[locale]
    }

    const domainTranslations = this.getDomainTranslations(locale)
    const dashboardTranslations = this.getDashboardTranslations(locale)
    const additionalTranslations = this.getAdditionalTranslations(locale)

    const merged = Object.assign(
      {},
      dashboardTranslations,
      additionalTranslations,
      ...domainTranslations
    )

    // Cache the result
    this.translationsCache[locale] = merged
    return merged
  }

  addTranslations(
    translationObject: Partial<Record<IPP_SUPPORTED_LOCALE, Record<string, string>>>
  ) {
    this.additionalTranslations['en-US'] = {
      ...this.additionalTranslations['en-US'],
      ...translationObject['en-US'],
    }

    if (translationObject['es-ES']) {
      this.additionalTranslations['es-ES'] = {
        ...this.additionalTranslations['es-ES'],
        ...translationObject['es-ES'],
      }
    }

    if (translationObject['fr-FR']) {
      this.additionalTranslations['fr-FR'] = {
        ...this.additionalTranslations['fr-FR'],
        ...translationObject['fr-FR'],
      }
    }
  }

  /**
   * Groups the retailer-scoped navigation entries from the domains by their v3 attach point.
   * Entries that omit `attachToV3` are skipped.
   */
  getDomainNavigationEntriesByAttachTypeV3(): Partial<
    Record<NavEntryPointV3, RPPNavigationEntry[]>
  > {
    const allNavEntries: Partial<Record<NavEntryPointV3, RPPNavigationEntry[]>> = {}

    const buildNavigationEntries = (
      domainId: string,
      navigationEntries: readonly RPPDomainNavigationEntryData[]
    ): Partial<Record<NavEntryPointV3, RPPNavigationEntry[]>> =>
      navigationEntries.reduce(
        (byAttachType, { subNavItems, attachToV3 }) => {
          if (!attachToV3) return byAttachType

          // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
          const newEntries: RPPNavigationEntry[] = subNavItems
            .map(subNavItem => {
              if (subNavItem.type !== 'item') return null
              const item = subNavItem as Extract<
                SubNavItemConfig<string, string, AccessControlConfig>,
                { type: 'item' }
              >
              return {
                route: item.route as string,
                llmDescription: item.llmDescription,
                subRoutes: item.subRoutes as string[],
                href: item.href as string,
                labelId: item.labelId,
                labelIdV3: item.labelIdV3,
                accessControl: item.accessControl,
                id: item.id,
                position: item.position,
                positionNavV3: item.positionNavV3,
                isNew: item.isNew,
                navBadge: item.navBadge,
                environment: item.environment,
                NavItemWrapper: item.NavItemWrapper,
                inToolPermissions: item.inToolPermissions,
                dividerBefore: item.dividerBefore,
                requiredTierV3: item.requiredTierV3,
                domainId,
              }
            })
            .filter(Boolean)

          byAttachType[attachToV3] = [...(byAttachType[attachToV3] || []), ...newEntries]
          return byAttachType
        },
        {} as Partial<Record<NavEntryPointV3, RPPNavigationEntry[]>>
      )

    const getEntryKey = (entry: RPPNavigationEntry) => {
      const permissionSlugs = entry.inToolPermissions?.map(({ slug }) => slug).join(',') ?? ''
      return [
        entry.domainId ?? '',
        entry.route,
        entry.labelId,
        entry.positionNavV3 ?? '',
        permissionSlugs,
      ].join('\0')
    }

    const mergeEntries = (source: Partial<Record<NavEntryPointV3, RPPNavigationEntry[]>>) => {
      Object.entries(source).forEach(([attachType, entries]) => {
        if (!entries) return
        const key = attachType as NavEntryPointV3
        const existingEntries = allNavEntries[key] || []
        const mergedEntries = [...existingEntries]
        const replacedIndices = new Set<number>()

        entries.forEach(entry => {
          const entryKey = getEntryKey(entry)
          const existingIndex = existingEntries.findIndex(
            (existing, index) => !replacedIndices.has(index) && getEntryKey(existing) === entryKey
          )
          if (existingIndex === -1) {
            mergedEntries.push(entry)
          } else {
            mergedEntries[existingIndex] = entry
            replacedIndices.add(existingIndex)
          }
        })

        allNavEntries[key] = mergedEntries
      })
    }

    this.navEntriesManifest?.forEach(({ id, navigationEntries }) => {
      mergeEntries(buildNavigationEntries(id, navigationEntries))
    })
    this.domains.forEach(domain => {
      if (domain.navigationEntries) {
        mergeEntries(
          buildNavigationEntries(
            domain.id,
            domain.navigationEntries as unknown as readonly RPPDomainNavigationEntryData[]
          )
        )
      }
    })

    return allNavEntries
  }

  /**
   * Groups the admin navigation entries from the domains by their v3 attach point.
   * Entries that omit `attachToV3` are skipped.
   */
  getDomainAdminNavigationEntriesByAttachTypeV3(): Partial<
    Record<AdminNavEntryPointV3, RPPNavigationEntry[]>
  > {
    return this.domains.reduce(
      (allNavEntries, domain) => {
        if (!domain.adminNavigationEntries) return allNavEntries

        domain.adminNavigationEntries.forEach(({ subNavItems, attachToV3 }) => {
          if (!attachToV3) return

          const newEntries: RPPNavigationEntry[] = subNavItems.map(subNavItem => ({
            route: subNavItem.route as string,
            subRoutes: subNavItem.subRoutes as string[],
            labelId: subNavItem.labelId,
            labelIdV3: subNavItem.labelIdV3,
            accessControl: subNavItem.accessControl,
            positionNavV3: subNavItem.positionNavV3,
            inToolPermissions: subNavItem.inToolPermissions,
            dividerBefore: subNavItem.dividerBefore,
            domainId: domain.id,
          }))

          allNavEntries[attachToV3] = [...(allNavEntries[attachToV3] || []), ...newEntries]
        })

        return allNavEntries
      },
      {} as Partial<Record<AdminNavEntryPointV3, RPPNavigationEntry[]>>
    )
  }

  /**
   * Merges all routes
   */
  getRoutes(): Record<string, string> {
    return this.domains.reduce((acc, domain) => ({ ...acc, ...domain.routes }), {
      ...this.routeCatalog,
      ...ROUTES_BY_NAME,
    })
  }

  /**
   * Recursively extract route-to-tool mappings from router JSX elements
   * This walks through the router tree and collects any DomainRoute elements that have a `tool` prop
   */
  private extractRouteToolsFromElement(
    element: ReactElement,
    routeToToolMap: Map<string, string>
  ): void {
    if (!isValidElement(element)) return

    // Check if this element has a route and tool prop
    const props = element.props as any
    if (props?.route && props?.tool) {
      routeToToolMap.set(props.route, props.tool)
    }

    // Recursively check children
    if (props?.children) {
      Children.forEach(props.children, child => {
        if (isValidElement(child)) {
          this.extractRouteToolsFromElement(child, routeToToolMap)
        }
      })
    }
  }

  /**
   * Extract route-to-tool mappings from all routers across all domains and dashboard routes
   */
  private extractRouteToolsFromRouters(): Map<string, string> {
    const routeToToolMap = new Map<string, string>()

    // Extract from domain routers
    this.domains.forEach(domain => {
      // Extract from all router types
      const allRouters = [
        ...(domain.routers.admin || []),
        ...(domain.routers.scoped || []),
        ...(domain.routers.partnerScoped || []),
        ...(domain.routers.public || []),
      ]

      allRouters.forEach(router => {
        if (isValidElement(router)) {
          this.extractRouteToolsFromElement(router, routeToToolMap)
        }
      })
    })

    // Extract from dashboard routes (core routes defined in packages/dashboard/src/routes)
    this.extractDashboardRouteTools(routeToToolMap)
    return routeToToolMap
  }

  /**
   * Extract tool props from dashboard routes using the static DASHBOARD_ROUTE_TOOLS registry
   * This captures routes like store-groups, orders, settings that are defined in the core dashboard
   *
   * NOTE: This is temporary until we move the tools out of dashboard into their respective domains.
   */
  private extractDashboardRouteTools(routeToToolMap: Map<string, string>): void {
    // Add dashboard route tools from the static registry
    Object.entries(DASHBOARD_ROUTE_TOOLS).forEach(([routeName, tool]) => {
      routeToToolMap.set(routeName, tool)

      // Validate that the route actually exists in ROUTES_BY_NAME
      if (!(ROUTES_BY_NAME as Record<string, unknown>)[routeName]) {
        console.warn(
          `[RPPCore] Dashboard route '${routeName}' is defined in DASHBOARD_ROUTE_TOOLS but not found in ROUTES_BY_NAME. ` +
            `This entry will be ignored.`
        )
      }
    })
  }

  /**
   * Maps routes to totem info for all domains and dashboard routes
   *
   * Priority order for DOMAIN routes:
   * 1. Route-level tool (defined on DomainRoute component `tool` prop) - extracted from JSX
   * 2. Fall back to domain default tool
   *
   * For DASHBOARD routes:
   * - Uses DASHBOARD_ROUTE_TOOLS static registry
   * - This is temporary until we move the tools out of dashboard into their respective domains.
   */
  getTotemInfoByRoute(): Record<
    string,
    { routePattern: string; hasDevTooling?: boolean; totemSystem?: TotemSystem }
  > {
    const routeOwnerMapping: Record<string, { routePattern: string; hasDevTooling?: boolean }> = {}

    // Extract route-level tools from router JSX elements (both domain and dashboard routes)
    const routeToolMap = this.extractRouteToolsFromRouters()

    // Process domain routes
    this.domains.forEach(domain => {
      Object.entries(domain.routes).forEach(([routeName, routePattern]) => {
        // Check route-level tool (from DomainRoute component `tool` prop)
        const routeLevelTool = routeToolMap.get(routeName)

        if (routeLevelTool) {
          routeOwnerMapping[routePattern as string] = {
            routePattern: routeLevelTool,
            hasDevTooling: domain.totem?.hasDevTooling,
          }
          return
        }

        // Fall back to domain default tool
        if (domain.totem?.tool) {
          routeOwnerMapping[routePattern as string] = {
            routePattern: domain.totem.tool,
            hasDevTooling: domain.totem.hasDevTooling,
          }
        }
      })
    })

    // Process dashboard routes (core routes from ROUTES_BY_NAME)
    Object.entries(ROUTES_BY_NAME).forEach(([routeName, routePattern]) => {
      // Only add dashboard routes that have an explicit tool prop
      const routeLevelTool = routeToolMap.get(routeName)
      if (routeLevelTool) {
        routeOwnerMapping[routePattern as string] = {
          routePattern: routeLevelTool,
          hasDevTooling: undefined, // Dashboard routes don't have domain-level hasDevTooling
        }
      }
    })

    setTotemRouteOwnerMapping(routeOwnerMapping)
    return routeOwnerMapping
  }

  /**
   * Extracts the admin routers of each domain and collects them into a flattened array
   */
  getAdminRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.admin'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the dev portal routers of each domain and collects them into a flattened array
   */
  getDevPortalRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.devPortal'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the scoped routers of each domain and collects them into a flattened array
   */
  getScopedRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.scoped'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the scoped routers of each domain and collects them into a flattened array
   */
  getPartnerScopedRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.partnerScoped'), flatten, compact])(this.domains)
  }

  /**
   * Extracts the public routers of each domain and collects them into a flattened array
   */
  getPublicRouters(): RPPRouter[] {
    return flow([a => map(a, 'routers.public'), flatten, compact])(this.domains)
  }

  prepareContext(): RPPCoreContext {
    return {
      routesByName: this.getRoutes(),
      adminRouters: this.getAdminRouters(),
      scopedRouters: this.getScopedRouters(),
      partnerScopedRouters: this.getPartnerScopedRouters(),
      publicRouters: this.getPublicRouters(),
      domainNavigationEntriesV3: this.getDomainNavigationEntriesByAttachTypeV3(),
      domainAdminNavigationEntriesV3: this.getDomainAdminNavigationEntriesByAttachTypeV3(),
      totemInfoByRoute: this.getTotemInfoByRoute(),
    }
  }

  /**
   * Start the app with lazy domain loading.
   * This allows the account details fetch to run in parallel with domain import,
   * potentially saving hundreds of milliseconds on initial load.
   *
   * Key optimization: ApolloProvider and AppProviderPreloader are placed ABOVE
   * the CoreContext.Provider, so the account fetch starts immediately while
   * domains are still loading.
   *
   * @param domainImportPromise - Promise that resolves to the domain registry module
   * @param callback - Render callback (receives React tree)
   */
  start<T extends (tree: ReactElement) => any>(
    domainImportPromise: Promise<{ registerDomains: (app: RPPCore) => void }>,
    callback: T
  ) {
    // Prefetch AppUIProviders chunk - starts download immediately while still lazy loaded.
    // By the time React.lazy needs it, the chunk will already be cached.
    importAppUIProviders()

    const history = createHistory()

    // Determine initial locale early so we can load lazy translations
    const initialLocale = getStoredLocaleWithFallback()

    // Promise that resolves when domains are registered AND lazy translations are loaded
    const domainRegistrationPromise = domainImportPromise.then(async ({ registerDomains }) => {
      registerDomains(this)
      this.attachCatalogDeps()
      // Domains whose components the eager shell renders (global menus) load at
      // boot: the shell has no route of its own to trigger route loading.
      await this.loadShellRequires()
      // Load only the initial route's own strings before first paint (en-US base plus the
      // active non-English locale). Every other domain's strings stay out of boot until
      // their route is entered.
      await this.loadTranslationsForRoute(history.location.pathname, initialLocale)
    })

    // Component that waits for domains before providing full context and rendering children
    // This is the "domain gate" - children only render after domains are registered
    const LazyContextProvider = ({ children }: { children: ReactElement }) => {
      const [contextValue, setContextValue] = useState<RPPCoreContext | null>(null)

      useEffect(() => {
        domainRegistrationPromise.then(() => {
          const ctx = this.prepareContext()
          setContextValue(ctx)
        })
        // Domains can also register ON DEMAND after boot (the native shell
        // registers the visited domain; the legacy registry loads when a legacy URL
        // needs it) — re-prepare the context each time the domain set changes.
        this.onDomainsLoaded(() => {
          setContextValue(this.prepareContext())
        })
      }, [])

      // Don't render children until context is ready (domains loaded)
      if (!contextValue) {
        return null
      }

      return <CoreContext.Provider value={contextValue}>{children}</CoreContext.Provider>
    }

    // Route-entry gate for en-US: on cross-domain navigation the target page's code is
    // eager but its strings may not be loaded yet. Hold render until they resolve so the
    // user never sees raw message-ids. Within-domain and already-loaded routes pass through
    // synchronously (no blank flash).
    const RouteTranslationsGate = ({ children }: { children: ReactElement }) => {
      const { pathname } = useLocation()
      const { locale } = useI18nContext()
      // Derived every render from the *current* route/locale — never a stale mount snapshot.
      // A stale `true` would let LocaleAwareApp build and cache a catalog missing the new
      // domain's strings, briefly flashing raw ids. `loaded` re-reads synchronously each render.
      const ready = this.routeTranslationsLoaded(pathname, locale)
      const [, forceRerender] = useState(0)
      const [failed, setFailed] = useState(false)

      useEffect(() => {
        if (ready) return
        let cancelled = false
        setFailed(false)
        this.loadTranslationsForRoute(pathname, locale)
          .then(() => {
            if (!cancelled) forceRerender(n => n + 1)
          })
          .catch(() => {
            // A lazy translation chunk failed to load (network / stale deploy).
            // Without this the gate holds forever and wedges a blank route.
            if (!cancelled) setFailed(true)
          })
        return () => {
          cancelled = true
        }
      }, [pathname, locale, ready])

      // Copy is hard-coded: the string catalog is exactly what failed to load, so
      // there is nothing localized to fall back on.
      if (failed && !ready) {
        return (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <p>Something went wrong loading this page.</p>
            <button type="button" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        )
      }

      // Skeleton while the target route's strings resolve. Uses the lean LoadingDots
      // (not gin-and-tonic LoadingPage, which drags the full shared-components +
      // @instacart/ids-customers barrels into the boot chunk).
      if (!ready)
        return (
          <div
            data-testid="route-translations-gate-loading"
            aria-busy="true"
            style={{ display: 'flex', justifyContent: 'center', padding: 24 }}
          >
            <LoadingDots />
          </div>
        )
      return children
    }

    // This wrapper provides locale-aware messages to AppProvider
    const LocaleAwareApp = () => {
      const { locale } = useI18nContext()
      loadDayjsLocale(locale)
      loadMomentLocale(locale)
      const messages = this.getTranslations(locale)
      return <AppProvider messages={messages} locale={locale} onError={this.devIntlOnError} />
    }

    // The legacy RRv5 tree. Behind the shell flag it renders inside a TanStack
    // catch-all route; with the flag off it renders directly under the RRv5
    // Router, byte-identical to the pre-shell mount.
    const legacyInner = (
      <>
        <Favicon url={RPP_FAVICON} />
        <AppProviderPreloader>
          <LazyContextProvider>
            <RouteTranslationsGate>
              <LocaleAwareApp />
            </RouteTranslationsGate>
          </LazyContextProvider>
        </AppProviderPreloader>
      </>
    )

    const routerTree = isTanstackShellEnabled() ? (
      <Suspense fallback={null}>
        {/* Native routes are a sibling of the legacy tree, so they need their own
            CoreContext: route-name resolution (url.ts `useCreateHrefFactory`) reads it.
            legacyInner carries its own provider below — this one covers the native
            branch. Account context (AppProviderPreloader) is mounted inside the shell's
            root layout instead — its DataPrefetcher reads RRv5 `useLocation`, which only
            resolves under the root `<LegacyRouter>`. */}
        <LazyContextProvider>
          <TanstackShell
            inner={legacyInner}
            history={history}
            loadNativeRoutes={this.loadNativeRoutes}
            getTranslations={locale => this.getTranslations(locale)}
            // Same instance the ApolloProvider below mounts — loaders must fill the cache
            // components actually read.
            apolloClient={fetchDashboardClient()}
            // The catch-all fires this when a legacy URL needs the RRv5 tree.
            ensureLegacyDomains={() => this.ensureLegacyDomains()}
          />
        </LazyContextProvider>
      </Suspense>
    ) : (
      <Router history={history}>{legacyInner}</Router>
    )

    // IMPORTANT: Structure is designed for optimal parallelization:
    // - ApolloProvider is above LazyContextProvider so Apollo queries can start immediately
    // - AppProviderPreloader starts account fetch while domains are still loading
    // - LazyContextProvider waits for domains before providing CoreContext
    // - AppProvider renders after both account AND domains are ready
    return callback(
      <I18nProvider>
        <ApolloProvider client={fetchDashboardClient()}>
          <QueryClientProvider client={queryClient}>{routerTree}</QueryClientProvider>
        </ApolloProvider>
      </I18nProvider>
    ) as ReturnType<T>
  }
}
